import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { colors } from 'app/shared/theme/colors';
import { useTranslation } from 'app/shared/i18n';

interface MailItem {
  from?: string;
  subject?: string;
  bodypreview?: string;
  bodyhtml?: string;
  code?: string;
  time?: string;
}
interface MailResult {
  emailpass: string;
  data?: MailItem[];
}

const senderBadge = (from = '') => {
  const f = from.toLowerCase();
  if (f.includes('facebook')) return { label: 'Facebook', color: '#1877F2' };
  if (f.includes('instagram')) return { label: 'Instagram', color: '#E4405F' };
  if (f.includes('tiktok')) return { label: 'TikTok', color: '#69C9D0' };
  if (f.includes('twitter') || f.includes('x.com')) return { label: 'X', color: '#1DA1F2' };
  if (f.includes('apple')) return { label: 'Apple', color: '#A2AAAD' };
  if (f.includes('amazon')) return { label: 'Amazon', color: '#FF9900' };
  if (f.includes('shopee')) return { label: 'Shopee', color: '#EE4D2D' };
  if (f.includes('lazada')) return { label: 'Lazada', color: '#0F136D' };
  if (f.includes('kakao')) return { label: 'Kakao', color: '#FEE500' };
  if (f.includes('telegram')) return { label: 'Telegram', color: '#0088CC' };
  if (f.includes('microsoft') || f.includes('outlook') || f.includes('hotmail')) return { label: 'Microsoft', color: '#00A4EF' };
  if (f.includes('google') || f.includes('gmail')) return { label: 'Google', color: '#EA4335' };
  return { label: 'Mail', color: colors.dim };
};

const extractEmail = (s = '') => {
  const m = s.match(/<([^>]+)>/);
  return m ? m[1] : s;
};

const oneLine: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const twoLine: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  maxHeight: 36,
  lineHeight: '18px',
};

export const ReadMail = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [inputListMail, setInputListMail] = useState('');
  const [listResult, setListResult] = useState<MailResult[]>([]);
  const [totalItemCheck, setTotalItemCheck] = useState(0);
  const [itemChecked, setItemChecked] = useState(0);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [detail, setDetail] = useState<MailItem | null>(null);

  const progress = useMemo(
    () => (totalItemCheck === 0 ? 0 : +((itemChecked / totalItemCheck) * 100).toFixed(1)),
    [itemChecked, totalItemCheck],
  );

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDetail(null);
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);

  const onClickCheckMail = async () => {
    setTotalItemCheck(0);
    setItemChecked(0);
    setLoading(true);
    setListResult([]);
    const dataValid = inputListMail.split('\n').filter(x => !!x.trim());
    setTotalItemCheck(dataValid.length);
    try {
      const { data: dataRes } = await axios.post('/api/tools/get-msg-mail', { emailpass: dataValid });
      const sessionId = dataRes.data;
      const eventSource = new EventSource(`/api/tools/get-msg-mail/stream?sessionId=${sessionId}`);
      eventSource.onmessage = event => {
        const data = JSON.parse(event.data);
        setListResult(prev => [...prev, data]);
        setItemChecked(prev => prev + 1);
      };
      eventSource.onerror = () => {
        eventSource.close();
        setLoading(false);
        Swal.fire({ title: t('toolCommon.complete'), icon: 'success' });
      };
    } catch {
      setLoading(false);
      Swal.fire({ title: t('toolCommon.errorOccurred'), icon: 'error' });
    }
  };

  const onCopy = (data: string) => {
    navigator.clipboard.writeText(data);
    toast.success(t('toolCommon.copySuccess'));
  };

  const toggleCollapse = (key: string) => setCollapsed(p => ({ ...p, [key]: !p[key] }));

  const totalMails = listResult.reduce((s, r) => s + (r.data?.length || 0), 0);
  const totalCodes = listResult.reduce((s, r) => s + (r.data?.filter(d => d.code).length || 0), 0);

  return (
    <div style={{ maxWidth: 1040, margin: '0 auto', padding: '36px 24px' }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
        📥 {t('toolReadMail.title')}
      </h2>
      <p style={{ color: colors.muted, fontSize: 13, margin: '0 0 20px' }}>
        {t('toolReadMail.subtitle', { defaultValue: 'Đọc nội dung hộp thư qua OAuth2 / IMAP' })}
      </p>

      <div className="ms-card" style={{ padding: 20, marginBottom: 20 }}>
        <textarea
          className="ms-textarea"
          style={{ height: 160, fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}
          value={inputListMail}
          placeholder="mail|pass|refresh_token|client_id"
          onChange={e => setInputListMail(e.target.value)}
        />
        <button className="ms-btn ms-btn--primary" onClick={onClickCheckMail} disabled={loading} style={{ width: '100%', marginTop: 12 }}>
          {loading ? `⏳ ${t('toolReadMail.reading')}` : `▶ ${t('toolReadMail.readMailbox')}`}
        </button>

        {(loading || progress > 0) && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 12, color: colors.dim }}>
                {t('toolCommon.progress')} · {itemChecked}/{totalItemCheck}
              </span>
              <span style={{ fontSize: 12, fontWeight: 600, color: colors.accent, fontFamily: "'JetBrains Mono', monospace" }}>
                {progress}%
              </span>
            </div>
            <div className="ms-progress">
              <div className="ms-progress__fill" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {listResult.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <span className="ms-badge" style={{ background: colors.surface, color: colors.muted }}>
            📬 {listResult.length} {t('toolReadMail.accounts', { defaultValue: 'tài khoản' })}
          </span>
          <span className="ms-badge" style={{ background: colors.infoDim, color: colors.info }}>
            ✉ {totalMails} mail
          </span>
          <span className="ms-badge ms-badge--success">🔑 {totalCodes} code</span>
        </div>
      )}

      {listResult.map(rs => {
        const email = rs.emailpass?.split('|')[0] || '';
        const mails = rs.data || [];
        const hasError = mails.length === 0;
        const isCollapsed = collapsed[email];
        return (
          <div key={rs.emailpass} className="ms-card" style={{ overflow: 'hidden', marginBottom: 14 }}>
            <div
              onClick={() => toggleCollapse(email)}
              style={{
                padding: '12px 16px',
                borderBottom: isCollapsed ? 'none' : `1px solid ${colors.border}`,
                background: colors.surface,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: 14 }}>{hasError ? '⚠' : '📬'}</span>
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: hasError ? colors.danger : colors.accent, ...oneLine }}>
                {email}
              </span>
              <span style={{ fontSize: 11, color: colors.dim }}>{mails.length} mail</span>
              <span
                style={{
                  fontSize: 11,
                  color: colors.dim,
                  transform: isCollapsed ? 'rotate(-90deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              >
                ▼
              </span>
            </div>

            {!isCollapsed && (
              <div style={{ padding: hasError ? 24 : 8 }}>
                {hasError ? (
                  <div style={{ textAlign: 'center', color: colors.muted, fontSize: 13 }}>{t('toolReadMail.noEmail')}</div>
                ) : (
                  mails.map((item, i) => {
                    const badge = senderBadge(item.from);
                    return (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          gap: 12,
                          padding: 12,
                          borderRadius: 8,
                          background: colors.bgCard,
                          border: `1px solid ${colors.border}`,
                          marginBottom: i === mails.length - 1 ? 0 : 8,
                        }}
                      >
                        <div
                          style={{
                            width: 36,
                            height: 36,
                            borderRadius: 8,
                            background: badge.color + '22',
                            color: badge.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            flexShrink: 0,
                          }}
                        >
                          {badge.label.slice(0, 2).toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
                            <span style={{ fontSize: 12, color: colors.muted, ...oneLine, flex: 1 }}>{extractEmail(item.from || '')}</span>
                            <span style={{ fontSize: 11, color: colors.dim, flexShrink: 0 }}>{item.time}</span>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, ...oneLine, marginBottom: 4 }}>
                            {item.subject || '(no subject)'}
                          </div>
                          {item.code && (
                            <div
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 12px',
                                marginBottom: 6,
                                background: colors.accentDim,
                                border: `1px dashed ${colors.accent}`,
                                borderRadius: 6,
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "'JetBrains Mono', monospace",
                                  fontSize: 16,
                                  fontWeight: 700,
                                  color: colors.accent,
                                  letterSpacing: 1,
                                }}
                              >
                                {item.code}
                              </span>
                              <button
                                className="ms-btn ms-btn--ghost"
                                onClick={() => onCopy(item.code)}
                                style={{ padding: '2px 8px', fontSize: 11 }}
                              >
                                📋
                              </button>
                            </div>
                          )}
                          <div style={{ fontSize: 12, color: colors.muted, ...twoLine }}>{item.bodypreview}</div>
                          {item.bodyhtml && (
                            <div style={{ marginTop: 8 }}>
                              <button
                                className="ms-btn ms-btn--ghost"
                                onClick={() => setDetail(item)}
                                style={{ padding: '4px 10px', fontSize: 11 }}
                              >
                                🔍 {t('toolReadMail.view')}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        );
      })}

      {detail && (
        <div className="ms-modal-overlay" onClick={() => setDetail(null)} style={{ padding: 16 }}>
          <div
            className="ms-modal"
            onClick={e => e.stopPropagation()}
            style={{ width: 640, maxWidth: '100%', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="ms-modal__header">
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
                <span style={{ fontSize: 11, color: colors.dim, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('toolReadMail.detail', { defaultValue: 'Chi tiết email' })}
                </span>
                <span style={{ fontSize: 14, fontWeight: 600, color: colors.text, ...oneLine }}>{detail.subject || '(no subject)'}</span>
              </div>
              <button className="ms-modal__close" onClick={() => setDetail(null)}>
                ✕
              </button>
            </div>

            <div className="ms-modal__body" style={{ overflow: 'auto', padding: 20 }}>
              {detail.code && (
                <div
                  style={{
                    padding: 14,
                    background: colors.accentDim,
                    border: `1px dashed ${colors.accent}`,
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 22,
                      fontWeight: 700,
                      color: colors.accent,
                      letterSpacing: 2,
                      flex: 1,
                    }}
                  >
                    {detail.code}
                  </span>
                  <button
                    className="ms-btn ms-btn--primary"
                    onClick={() => onCopy(detail.code)}
                    style={{ padding: '6px 14px', fontSize: 12 }}
                  >
                    📋 Copy
                  </button>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: '8px 12px', fontSize: 12, marginBottom: 14 }}>
                <span style={{ color: colors.dim }}>From</span>
                <span style={{ color: colors.text, ...oneLine }}>{detail.from}</span>
                <span style={{ color: colors.dim }}>Time</span>
                <span style={{ color: colors.text }}>{detail.time}</span>
              </div>

              <div
                style={{
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                <iframe
                  title="mail-body"
                  sandbox=""
                  srcDoc={detail.bodyhtml}
                  style={{ width: '100%', height: 320, border: 'none', display: 'block' }}
                />
              </div>
            </div>

            <div
              style={{
                padding: '12px 20px',
                borderTop: `1px solid ${colors.border}`,
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
              }}
            >
              {detail.code && (
                <button className="ms-btn ms-btn--ghost" onClick={() => onCopy(detail.code)}>
                  Copy code
                </button>
              )}
              <button className="ms-btn ms-btn--ghost" onClick={() => setDetail(null)}>
                {t('toolCommon.close', { defaultValue: 'Đóng' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
